import {authorize} from 'authorize';
import {COURSES_MODULE, EXAM_SYSTEM_MODULE, PARTICIPANT_DRIVEN_LEARNING} from 'constants/LU_AppFeatures';

/**
 * Returns true if the client has the specified app feature.
 *
 * @param appFeature An app feature ref string
 * @todo Reimplement without using `authorize`
 */
export const clientHasAppFeature = (appFeature: string) => {
    return authorize(null, null, appFeature);
};

/**
 * Returns true if the client has all of the specified app features.
 *
 * @param appFeatures One or more app feature ref strings
 */
export const clientHasAppFeatures = (appFeatures: string[]) => {
    return appFeatures.every(clientHasAppFeature);
};

export const getClientTier = () => {
    if (!authorize(null, null, COURSES_MODULE)) {
        return 'Pro';
    }

    if (!authorize(null, null, EXAM_SYSTEM_MODULE)) {
        return 'Enterprise';
    }

    return 'Exam';
};

/**
 * Checks to see if the client is a Pro Client.
 *
 * @returns True if the client in the UI is logged into is a Pro Client, false otherwise
 * @todo Reimplement without using `authorize`
 */
export const isProClient = () => {
    return !authorize(null, null, COURSES_MODULE);
};

/**
 * Checks to see if the client is an Enterprise Client.
 *
 * @returns True if the client in the UI is logged into is an Enterprise Client, false otherwise
 * @todo Reimplement without using `authorize`
 */
export const isEnterpriseClient = () => {
    return authorize(null, null, COURSES_MODULE) && !authorize(null, null, EXAM_SYSTEM_MODULE);
};

/**
 * Checks to see if the client is a skills Client.
 *
 * @returns {boolean} True if the client in the UI is logged into is a skills Client, false otherwise
 */
export const isSkillsClient = () => {
    return authorize(null, null, PARTICIPANT_DRIVEN_LEARNING);
};

/**
 * Checks whether the user has the permission globally.
 *
 * @param permission A permission ref string
 * @returns True if the specified permission is granted globally.
 */
export const userHasGlobalPermission = (permission: string) => {
    return authorize(permission);
};

/**
 * Checks whether the user has the permission globally.
 *
 * @param permissions An array of permission ref strings
 * @returns True if any of the specified permissions is granted globally.
 */
export const userHasSomeGlobalPermissions = (permissions: string[]) => {
    return authorize(permissions);
};

/**
 * Wrapper around legacy `authorize`` that imposes a stricter and more explicit API for permissions checking.
 *
 * @param instance An immutable model instance with an `acl` property
 * @param permissions One or more permission ref strings
 * @returns True if any of the specified permissions are granted either on the instance `acl` or globally
 */
export const userHasSomePermissionsOn = (instance: Immutable.Map<unknown, unknown>, permissions: string[]) => {
    return authorize(permissions, instance);
};

/**
 * Checks whether the user has the permission on the specified instance.
 *
 * @param instance An immutable model instance with an `acl` property
 * @param permission A permission ref string
 * @returns True if the specified permission is granted either on the instance `acl` or globally
 */
export const userHasPermissionOn = (instance: Immutable.Map<unknown, unknown>, permission: string) => {
    return userHasSomePermissionsOn(instance, [permission]);
};
